import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import DashboardService from '../../services/dashboard/Dashboard';

const RankUsers = ({ ranksHeaders }) => {
    const [activeTab, setActiveTab] = useState(ranksHeaders[0]?.id);
    const observer = useRef();

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        refetch
    } = useInfiniteQuery({
        queryKey: ['get-rank-achievers-infinite', activeTab],
        queryFn: ({ pageParam = 1 }) => DashboardService.GetRankAchieversWithPagination(activeTab, pageParam, 10),
        getNextPageParam: (lastPage, allPages) => {
            // If last page has data and is full (10 items per page), there might be more
            if (lastPage?.Users && lastPage.Users.length === 10) {
                return allPages.length + 1;
            }
            return undefined; // No more pages
        },
        enabled: !!activeTab, // Only run when activeTab is available
    });

    // Flatten all pages into a single array
    const allUsers = data?.pages?.flatMap(page => page?.Users || []) || [];

    // Last element ref for intersection observer
    const lastUserElementRef = useCallback(node => {
        if (isFetchingNextPage) return;
        if (observer.current) observer.current.disconnect();
        
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasNextPage) {
                fetchNextPage();
            }
        });
        
        if (node) observer.current.observe(node);
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Refetch will automatically happen due to queryKey change
    };

    return (
        <div className="col-md-8">
            <div className="joinings_viewBox teamperfomance mb-3" style={{ minHeight: '400px' }}>
                <div className="joinings_viewBox_head">
                    <h5>Ranks</h5>
                </div>
                <ul
                    className="teamPerfomance_tab nav nav-tabs mb-3"
                    id="ex1"
                    role="tablist"
                >
                    {ranksHeaders?.map((rank) => (
                        <li className="nav-item" role="presentation" key={rank}>
                            <Link
                                className={`nav-link ${activeTab === rank.id ? "active" : ""}`}
                                id={`ex1-tab-${rank.id}`}
                                data-bs-toggle="tab"
                                role="tab"
                                aria-controls={rank.id}
                                aria-selected={activeTab === rank.id ? "true" : "false"}
                                onClick={() => handleTabChange(rank.id)}
                                style={{ textTransform: 'capitalize' }}
                            >
                                {rank.name} {/* ({rank.usersCount}) */}
                            </Link>
                        </li>
                    ))}
                </ul>
                <div className='row' style={{ overflowY: 'scroll', maxHeight: '300px' }}>
                    {isError && (
                        <div className="col-md-12 text-center py-3">
                            <p className="text-danger">Error loading rank achievers</p>
                        </div>
                    )}

                    {allUsers?.map((user, index) => (
                        <div 
                            className="col-md-4 mb-3" 
                            key={user.id || `user-${index}`}
                            ref={allUsers.length === index + 1 ? lastUserElementRef : null}
                        >
                            <div className='row rank-user-container'>
                                <div className='col-md-6 rank-user-image'>
                                    <img
                                        style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff' }}
                                        src={user.UserDetail?.image ?? "/images/user-profile.png"}
                                        alt=""
                                    />
                                </div>
                                <div className='col-md-6 rank-user-text'>{user.UserDetail?.name}</div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Initial loading */}
                    {isLoading && (
                        <div className="col-md-12 text-center py-3">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}
                    
                    {/* Loading next page */}
                    {isFetchingNextPage && (
                        <div className="col-md-12 text-center py-3">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="sr-only">Loading more...</span>
                            </div>
                        </div>
                    )}
                    
                    {/* End of data indicator */}
                    {!hasNextPage && allUsers.length > 0 && (
                        <div className="col-md-12 text-center py-3">
                            <p className="text-muted">No more users to load</p>
                        </div>
                    )}
                    
                    {/* No data indicator */}
                    {!isLoading && allUsers.length === 0 && !isError && (
                        <div className="col-md-12 text-center py-3">
                            <p className="text-muted">No rank achievers found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RankUsers;